import { FC, useEffect, useState } from 'react';
import { coreService } from 'network-utils';
import { useSelector } from 'react-redux';
import { selectIsLeanUser, selectToken } from '@redux/slices/userSlice';
import convertCourseToContentCard from '@services/convertCourseToContentCard';
import convertLessonToContentCard from '@services/convertLessonToContentCard';
import convertSkillTreeToContentCard from '@services/convertSkillTreeToContentCard';
import convertLearningPathToContentCard from '@services/convertLearningPathToContentCard';
import convertVideosToContentCard from '@services/convertVideosToContentCard';
import convertLabToContentCard from '@services/convertLabToContentCard';
import { getPlatformUrl } from 'utils';
import { useGetDiscoverContent, useGetNewCourses } from '@queries/core/discover';
import { useGetCategories } from '@queries/core/tagging';
import Discover from './discover';

const DiscoverContainer: FC<DiscoverPageProps> = (props: DiscoverPageProps) => {
    const { onContactUsClick, setCertificateCourseUuid, setIsCustomCourse, setVideoTutorialUuid } = props;
    const [categories, setCategories] = useState<coreService.CategoryWithTags[]>([]);
    const [newCourses, setNewCourses] = useState<coreService.CourseHighlight[]>([]);
    const [discoveryData, setDiscoveryData] = useState<any>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [sort, setSort] = useState<string>('');
    const [page, setPage] = useState<number>(0);
    const [total, setTotal] = useState<number>(0);
    const [contentTypes, setContentTypes] = useState<string[]>([]);
    const token = useSelector(selectToken);
    const isLeanUser = useSelector(selectIsLeanUser);
    const PER_PAGE_FETCHING = 24;

    const { data: categoriesData, isLoading: isLoadingCategories } = useGetCategories(token, false);
    const { data: newCoursesData, isLoading: isLoadingNewCourses } = useGetNewCourses(token);
    const { data: discoverContentData, isLoading: isLoadingDiscoverContentData } = useGetDiscoverContent(token, {
        sort,
        tags: selectedTags,
        category: selectedCategory,
        contentTypes,
        offset: page * PER_PAGE_FETCHING,
        limit: PER_PAGE_FETCHING
    });

    useEffect(() => {
        setSelectedTags([]);
    }, [selectedCategory]);

    useEffect(() => {
        setPage(0);
    }, [selectedCategory, selectedTags, sort, contentTypes]);

    const types = {
        Course: 'course',
        Lesson: 'lesson',
        Challenge: 'challenge',
        VideoTutorial: 'videoTutorial',
        SkillTree: 'skillTree',
        LearningPath: 'learningPath',
        Lab: 'lab'
    };

    useEffect(() => {
        if (discoverContentData?.content && !isLoadingDiscoverContentData) {
            const contentTypeHandlers: { [key: string]: Function } = {
                [coreService.TaggingsContentTypes.COURSE]: (data: coreService.Course[]) =>
                    data.map((course: coreService.Course) =>
                        convertCourseToContentCard(course, isLeanUser, setCertificateCourseUuid, setIsCustomCourse)
                    ),
                [coreService.TaggingsContentTypes.LESSON]: (data: coreService.Lesson[]) =>
                    data.map((lesson: coreService.Lesson) =>
                        convertLessonToContentCard(lesson, isLeanUser, onContactUsClick)
                    ),
                [coreService.TaggingsContentTypes.LAB]: (data: coreService.LabContentCard[]) =>
                    data.map((lab: coreService.LabContentCard) => convertLabToContentCard(lab)),
                [coreService.TaggingsContentTypes.CHALLENGE]: (data: coreService.ChallengeData[]) =>
                    data.map((challenge: coreService.ChallengeData) => ({
                        uuid: challenge.uuid,
                        displayName: challenge.displayName,
                        imageUrl: `${process.env.PUBLIC_URL}/images/challengeBackground.svg`,
                        isTaken: challenge.taken,
                        score: challenge.highestScore,
                        level: challenge.level,
                        language: challenge.language,
                        cardUrl: `${getPlatformUrl()}${challenge.url}`,
                        maxScore: challenge.maxScore,
                        type: 'challenge',
                        tags: challenge.tags?.map((tag: coreService.Tag) => tag.displayName)
                    })),
                [coreService.TaggingsContentTypes.VIDEO_TUTORIAL]: (data: coreService.VideoTutorialData[]) =>
                    data.map((videoTutorial: coreService.VideoTutorialData) =>
                        convertVideosToContentCard(videoTutorial, setVideoTutorialUuid)
                    ),
                [coreService.TaggingsContentTypes.SKILL_TREE]: (data: coreService.SkillTree[]) =>
                    data.map((skillTree: coreService.SkillTree) => convertSkillTreeToContentCard(skillTree)),
                [coreService.TaggingsContentTypes.LEARNING_PATH]: (data: coreService.LearningPath[]) =>
                    data.map((learningPath: coreService.LearningPath) => convertLearningPathToContentCard(learningPath))
            };

            const allCards = discoverContentData?.content
                .filter(content => content.data?.length > 0)
                .flatMap(content => {
                    const handler = contentTypeHandlers[types[content.contentType]];
                    return handler ? handler(content.data) : [];
                });
            if (page === 0) {
                setDiscoveryData(allCards);
                setTotal(discoverContentData?.total);
            } else {
                setDiscoveryData([...discoveryData, ...allCards]);
            }
        }
    }, [
        discoverContentData,
        isLeanUser,
        setCertificateCourseUuid,
        setIsCustomCourse,
        setVideoTutorialUuid,
        onContactUsClick
    ]);

    useEffect(() => {
        if (!categoriesData) return;
        setCategories(categoriesData);
    }, [categoriesData]);

    useEffect(() => {
        if (!newCoursesData?.courses) return;
        setNewCourses(newCoursesData.courses);
    }, [newCoursesData]);

    return (
        <Discover
            newCourses={newCourses}
            isLoadingCategories={isLoadingCategories}
            isLoadingNewCourses={isLoadingNewCourses}
            categories={categories}
            setSelectedCategory={setSelectedCategory}
            selectedCategory={selectedCategory}
            selectedContentType={contentTypes}
            setSelectedContentType={setContentTypes}
            selectedTags={selectedTags}
            setSelectedTags={setSelectedTags}
            discoveryData={discoveryData}
            onLoadMore={() => {
                if (discoveryData.length < total) setPage(page + 1);
            }}
            total={total}
            isLoadingDiscoveryData={isLoadingDiscoverContentData}
        />
    );
};

interface DiscoverPageProps {
    setIsPrimaryCourseDialogOpen: Function;
    setCertificateCourseUuid: Function;
    setIsCustomCourse: Function;
    setLearningPathCertificateDate: Function;
    setSkillTreeCertificateDate: Function;
    setVideoTutorialUuid: Function;
    onContactUsClick: Function;
}
export default DiscoverContainer;
